import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, ArrowRight, Printer, Warning, PencilSimple, Eye, Copy, Archive, Trash, DotsThreeVertical, ClockCounterClockwise, ClipboardText } from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

const STAGES = ["Preparation", "Cooking", "Chilling", "FreezeDrying", "Packaging", "Completed"];

export default function Batches() {
  const [batches, setBatches] = useState([]);
  const [skus, setSkus] = useState([]);
  const [machines, setMachines] = useState([]);
  const [open, setOpen] = useState(false);
  const [advance, setAdvance] = useState(null);
  const [form, setForm] = useState({ sku_id: "", planned_qty_kg: 10 });
  const [shortfall, setShortfall] = useState(null);
  const [editBatch, setEditBatch] = useState(null);
  const [viewBatch, setViewBatch] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // {batch, action:'archive'|'delete'|'duplicate'}
  const [editForm, setEditForm] = useState({});
  const [audit, setAudit] = useState([]);
  const [advForm, setAdvForm] = useState({});
  // QC prompt after stage advance
  const [qcPrompt, setQcPrompt] = useState(null); // { batch, stage, template, checks, notes, result }

  const load = async () => {
    setBatches((await api.get("/batches")).data);
    setSkus((await api.get("/skus")).data);
    setMachines((await api.get("/machines")).data);
  };
  useEffect(() => { load(); }, []);

  const create = async (allow_shortfall=false) => {
    try {
      await api.post("/batches", { ...form, allow_shortfall });
      toast.success("Batch started · Raw materials consumed");
      setOpen(false); setShortfall(null); setForm({ sku_id: "", planned_qty_kg: 10 }); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.shortfalls) {
        setShortfall(d.shortfalls);
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || e.message));
      }
    }
  };

  const doAdvance = async () => {
    try {
      const advancedBatch = advance;
      const nextStage = STAGES[STAGES.indexOf(advancedBatch.stage) + 1] || "Completed";
      const { data: updated } = await api.post(`/batches/${advancedBatch.id}/advance`, advForm);
      toast.success(`Advanced from ${advancedBatch.stage}`);
      setAdvance(null); setAdvForm({}); load();
      // After successful advance, check if a SKU-bound QC template exists for the new stage
      try {
        const { data: templates } = await api.get(`/qc-templates/for-batch/${advancedBatch.id}`,
                                                    { params: { stage: nextStage } });
        const recommended = (templates || []).find(t => t._recommended) || (templates || [])[0];
        if (recommended) {
          const checks = (recommended.checkpoints || []).map(c => ({
            key: c.key, label: c.label, kind: c.kind, unit: c.unit,
            min: c.min, max: c.max, required: c.required, options: c.options, value: "",
          }));
          setQcPrompt({
            batch: updated || advancedBatch,
            stage: nextStage,
            template: recommended,
            checks, notes: "", result: "Passed", moisture: "", weight: "",
          });
        }
      } catch { /* silent — QC prompt is best-effort */ }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const submitQc = async () => {
    try {
      const body = {
        batch_id: qcPrompt.batch.id, batch_no: qcPrompt.batch.batch_no,
        stage: qcPrompt.stage, result: qcPrompt.result,
        moisture: qcPrompt.moisture !== "" ? parseFloat(qcPrompt.moisture) : null,
        weight: qcPrompt.weight !== "" ? parseFloat(qcPrompt.weight) : null,
        notes: qcPrompt.notes, template_id: qcPrompt.template.id, checks: qcPrompt.checks,
      };
      await api.post("/qc", body);
      toast.success(`QC recorded for ${qcPrompt.batch.batch_no}`);
      setQcPrompt(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const updQcCheck = (i, val) => {
    const c = [...qcPrompt.checks]; c[i].value = val;
    setQcPrompt({ ...qcPrompt, checks: c });
  };

  const openEdit = async (b) => {
    setEditBatch(b);
    setEditForm({ planned_qty_kg: b.planned_qty_kg, priority: b.priority || "Normal",
                   notes: b.notes || "", supervisor: b.supervisor || "" });
  };
  const saveEdit = async () => {
    try {
      await api.put(`/batches/${editBatch.id}`, editForm);
      toast.success("Batch updated successfully.");
      setEditBatch(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const openView = async (b) => {
    setViewBatch(b);
    try { const r = await api.get(`/batches/${b.id}/audit`); setAudit(r.data); }
    catch { setAudit([]); }
  };
  const doArchive = async (b) => {
    try {
      await api.post(`/batches/${b.id}/archive`);
      toast.success(`Batch ${b.batch_no} archived.`);
      setConfirmAction(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const doDelete = async (b) => {
    try {
      await api.delete(`/batches/${b.id}`);
      toast.success(`Batch ${b.batch_no} deleted.`);
      setConfirmAction(null); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Delete blocked — use Archive instead."));
    }
  };
  const doDuplicate = async (b) => {
    try {
      const r = await api.post(`/batches/${b.id}/duplicate`);
      toast.success(`Duplicated as ${r.data.batch_no}`);
      setConfirmAction(null); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Duplicate failed"));
    }
  };

  return (
    <div data-testid="batches-page">
      <PageHeader subtitle="Manufacturing" title="Production Batches"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-batch-btn"><Plus size={16} className="mr-1.5"/>Start Batch</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Start Production Batch</DialogTitle><DialogDescription className="sr-only">Start Production Batch</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>SKU</Label>
                  <Select value={form.sku_id} onValueChange={v=>setForm({...form, sku_id:v})}>
                    <SelectTrigger data-testid="batch-sku"><SelectValue placeholder="Select SKU"/></SelectTrigger>
                    <SelectContent className="max-h-72">{skus.map(s=><SelectItem key={s.id} value={s.id}>{s.code} · {s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Planned Qty (kg)</Label>
                  <Input type="number" value={form.planned_qty_kg} onChange={e=>setForm({...form, planned_qty_kg:parseFloat(e.target.value)})} data-testid="batch-qty"/>
                </div>
              </div>
              <DialogFooter><Button onClick={()=>create(false)} disabled={!form.sku_id} data-testid="batch-save">Start Batch</Button></DialogFooter>
              {shortfall && (
                <div className="mt-3 border border-amber-300 bg-amber-50 rounded-md p-3 text-sm">
                  <div className="flex items-center gap-2 text-amber-800 font-semibold mb-2"><Warning size={16}/>Insufficient raw material stock</div>
                  <ul className="text-xs space-y-1 text-amber-900">
                    {shortfall.map((s, i) => (
                      <li key={i}>• <b>{s.material}</b> — need {s.need} {s.unit}, have {s.have} {s.unit}</li>
                    ))}
                  </ul>
                  <Button size="sm" variant="outline" className="mt-2" onClick={()=>create(true)} data-testid="batch-force">
                    Start Anyway (allow negative stock)
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="shadow-none">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Batch No</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Planned kg</TableHead>
                <TableHead className="text-right">Dry kg</TableHead>
                <TableHead className="text-right">Yield %</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>QC</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {batches.map(b => (
                <TableRow key={b.id} data-testid={`batch-row-${b.batch_no}`}>
                  <TableCell className="mono font-semibold">{b.batch_no}</TableCell>
                  <TableCell><div className="text-sm">{b.sku_name}</div><div className="text-xs text-muted-foreground mono">{b.sku_code}</div></TableCell>
                  <TableCell className="text-right mono">{b.planned_qty_kg}</TableCell>
                  <TableCell className="text-right mono">{b.dry_weight_kg || "—"}</TableCell>
                  <TableCell className="text-right mono">{b.yield_pct || "—"}</TableCell>
                  <TableCell><StatusBadge status={b.stage}/></TableCell>
                  <TableCell><StatusBadge status={b.qc_status}/></TableCell>
                  <TableCell><StatusBadge status={b.status}/></TableCell>
                  <TableCell className="space-x-1 whitespace-nowrap text-right">
                    {b.stage !== "Completed" && (
                      <Button size="sm" variant="outline" onClick={()=>{setAdvance(b); setAdvForm({});}} data-testid={`advance-${b.batch_no}`}>
                        Advance <ArrowRight size={12} className="ml-1"/>
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={()=>openView(b)}
                            title="View" data-testid={`batch-view-${b.batch_no}`}><Eye size={15}/></Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={()=>openEdit(b)}
                            title="Edit" data-testid={`batch-edit-${b.batch_no}`}><PencilSimple size={15}/></Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`batch-actions-${b.batch_no}`}>
                          <DotsThreeVertical size={16} weight="bold"/>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={()=>setConfirmAction({batch:b, action:"duplicate"})}
                                          data-testid={`batch-duplicate-${b.batch_no}`}>
                          <Copy size={14} className="mr-2"/>Duplicate
                        </DropdownMenuItem>
                        {b.status !== "Archived" && b.stage !== "Preparation" && (
                          <DropdownMenuItem onSelect={()=>setConfirmAction({batch:b, action:"archive"})}
                                            data-testid={`batch-archive-${b.batch_no}`}>
                            <Archive size={14} className="mr-2"/>Archive
                          </DropdownMenuItem>
                        )}
                        {b.stage === "Preparation" && b.qc_status !== "Failed" && (
                          <>
                            <DropdownMenuSeparator/>
                            <DropdownMenuItem onSelect={()=>setConfirmAction({batch:b, action:"delete"})} className="text-red-600"
                                              data-testid={`batch-delete-${b.batch_no}`}>
                              <Trash size={14} className="mr-2"/>Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {(b.stage === "Packaging" || b.stage === "Completed") && (
                      <Button size="sm" variant="ghost" asChild data-testid={`labels-${b.batch_no}`}>
                        <Link to={`/labels/${b.batch_no}`}><Printer size={14} className="mr-1"/>Labels</Link>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!advance} onOpenChange={v=>!v && setAdvance(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Advance Stage — {advance?.batch_no}</DialogTitle><DialogDescription className="sr-only">Advance Stage — {advance?.batch_no}</DialogDescription></DialogHeader>
          {advance && (
            <div className="space-y-3">
              <div className="p-3 rounded-md bg-muted flex items-center justify-between text-sm">
                <span className="font-semibold">{advance.stage}</span>
                <ArrowRight size={16}/>
                <span className="font-semibold text-primary">{STAGES[STAGES.indexOf(advance.stage)+1] || "Completed"}</span>
              </div>
              {advance.stage === "Cooking" && (
                <div><Label>Cooked Weight (kg)</Label><Input type="number" onChange={e=>setAdvForm({...advForm, cooked_weight_kg:parseFloat(e.target.value)})} data-testid="adv-cooked"/></div>
              )}
              {advance.stage === "Chilling" && (
                <div>
                  <Label>Freeze Dryer</Label>
                  <Select onValueChange={v=>{const m=machines.find(x=>x.id===v); setAdvForm({...advForm, machine_id:v, machine_name:m?.name});}}>
                    <SelectTrigger><SelectValue placeholder="Choose machine"/></SelectTrigger>
                    <SelectContent>{machines.map(m=><SelectItem key={m.id} value={m.id}>{m.name} ({m.capacity_kg}kg)</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {advance.stage === "FreezeDrying" && (
                <>
                  <div><Label>Dry Weight (kg)</Label><Input type="number" onChange={e=>setAdvForm({...advForm, dry_weight_kg:parseFloat(e.target.value)})} data-testid="adv-dry"/></div>
                  <div><Label>Moisture %</Label><Input type="number" onChange={e=>setAdvForm({...advForm, moisture_pct:parseFloat(e.target.value)})}/></div>
                </>
              )}
              {advance.stage === "Packaging" && (
                <div><Label>Packed Qty (pouches)</Label><Input type="number" onChange={e=>setAdvForm({...advForm, packed_qty:parseInt(e.target.value)})} data-testid="adv-packed"/></div>
              )}
              <div><Label>Remarks</Label><Textarea rows={2} onChange={e=>setAdvForm({...advForm, remarks:e.target.value})}/></div>
            </div>
          )}
          <DialogFooter><Button onClick={doAdvance} data-testid="adv-confirm">Confirm Advance</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Edit Batch dialog --- */}
      <Dialog open={!!editBatch} onOpenChange={v=>!v && setEditBatch(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PencilSimple size={17}/>Edit Batch · {editBatch?.batch_no}</DialogTitle>
            <DialogDescription>
              Batch number and stage cannot be changed. Edits are audit-logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="overline text-[10px]">Planned Qty (kg)</Label>
              <Input type="number" value={editForm.planned_qty_kg ?? ""}
                     onChange={e=>setEditForm({...editForm, planned_qty_kg: parseFloat(e.target.value)||0})}
                     className="mono" data-testid="batch-edit-qty"/></div>
            <div><Label className="overline text-[10px]">Priority</Label>
              <Select value={editForm.priority || "Normal"} onValueChange={v=>setEditForm({...editForm, priority: v})}>
                <SelectTrigger className="h-9" data-testid="batch-edit-priority"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {["Low", "Normal", "High", "Critical"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="overline text-[10px]">Supervisor</Label>
              <Input value={editForm.supervisor || ""}
                     onChange={e=>setEditForm({...editForm, supervisor: e.target.value})}
                     data-testid="batch-edit-supervisor"/></div>
            <div><Label className="overline text-[10px]">Notes</Label>
              <Textarea rows={2} value={editForm.notes || ""}
                        onChange={e=>setEditForm({...editForm, notes: e.target.value})}
                        data-testid="batch-edit-notes"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditBatch(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="batch-save-edit">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- View Batch dialog with audit --- */}
      <Dialog open={!!viewBatch} onOpenChange={v=>!v && setViewBatch(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye size={17}/>Batch · {viewBatch?.batch_no}
              {viewBatch?.status && <Badge variant="secondary" className="uppercase">{viewBatch.status}</Badge>}
            </DialogTitle>
            <DialogDescription>Read-only view with the batch lifecycle and audit log.</DialogDescription>
          </DialogHeader>
          {viewBatch && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="overline block">SKU</span>{viewBatch.sku_name} <span className="mono text-muted-foreground text-xs">({viewBatch.sku_code})</span></div>
                <div><span className="overline block">Stage</span><StatusBadge status={viewBatch.stage}/></div>
                <div><span className="overline block">Planned kg</span><span className="mono">{viewBatch.planned_qty_kg}</span></div>
                <div><span className="overline block">Dry kg</span><span className="mono">{viewBatch.dry_weight_kg || "—"}</span></div>
                <div><span className="overline block">Yield %</span><span className="mono">{viewBatch.yield_pct || "—"}</span></div>
                <div><span className="overline block">QC</span><StatusBadge status={viewBatch.qc_status}/></div>
                <div><span className="overline block">Machine</span>{viewBatch.machine_name || "—"}</div>
                <div><span className="overline block">Trays</span><span className="mono">{(viewBatch.tray_numbers||[]).join(", ") || "—"}</span></div>
                <div><span className="overline block">Priority</span>{viewBatch.priority || "Normal"}</div>
                <div><span className="overline block">Supervisor</span>{viewBatch.supervisor || "—"}</div>
              </div>
              {viewBatch.notes && (
                <div className="p-2 rounded bg-muted text-xs"><span className="font-semibold">Notes:</span> {viewBatch.notes}</div>
              )}
              <div>
                <div className="overline mb-1.5 flex items-center gap-1"><ClockCounterClockwise size={12}/>Audit trail ({audit.length})</div>
                {audit.length === 0 ? <div className="text-xs text-muted-foreground italic">No changes recorded yet.</div> : (
                  <div className="space-y-1 max-h-52 overflow-auto">
                    {audit.map(a => (
                      <div key={a.id} className="text-xs flex items-center gap-2 border-l-2 border-primary/30 pl-2 py-1" data-testid={`batch-audit-${a.id}`}>
                        <Badge variant="secondary" className="text-[9px] uppercase">{a.action.replace("_"," ")}</Badge>
                        <span className="text-muted-foreground mono">{new Date(a.at).toLocaleString()}</span>
                        <span>by {a.user}</span>
                        {a.changes && Object.keys(a.changes).length > 0 && (
                          <span className="text-muted-foreground truncate italic">{JSON.stringify(a.changes).slice(0,60)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={()=>setViewBatch(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Confirm dialog (archive / delete / duplicate) --- */}
      <Dialog open={!!confirmAction} onOpenChange={v=>!v && setConfirmAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmAction?.action === "archive" && <><Archive size={17}/>Archive Batch?</>}
              {confirmAction?.action === "delete" && <><Trash size={17} className="text-red-600"/>Delete Batch?</>}
              {confirmAction?.action === "duplicate" && <><Copy size={17}/>Duplicate Batch?</>}
            </DialogTitle>
            <DialogDescription>
              {confirmAction?.action === "archive" && "Archived batches are hidden from active lists but stay in the database for audit. Cannot be un-done from the UI."}
              {confirmAction?.action === "delete" && "Permanent deletion is only allowed for batches still on Preparation with no QC decision. Otherwise the delete will fail and you should Archive instead."}
              {confirmAction?.action === "duplicate" && `Creates a new batch with the same SKU (${confirmAction?.batch?.sku_name}) and planned quantity (${confirmAction?.batch?.planned_qty_kg} kg). Raw material stock will be consumed.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setConfirmAction(null)}>Cancel</Button>
            <Button variant={confirmAction?.action === "delete" ? "destructive" : "default"}
                    onClick={() => confirmAction?.action === "archive" ? doArchive(confirmAction.batch)
                                : confirmAction?.action === "delete" ? doDelete(confirmAction.batch)
                                : doDuplicate(confirmAction.batch)}
                    data-testid="batch-confirm-action">
              {confirmAction?.action === "archive" ? "Archive" : confirmAction?.action === "delete" ? "Delete permanently" : "Duplicate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* --- QC Auto-prompt after stage advance --- */}
      <Dialog open={!!qcPrompt} onOpenChange={v => !v && setQcPrompt(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardText size={18} className="text-emerald-700"/>
              Record QC for {qcPrompt?.batch?.batch_no}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Batch advanced to <b>{qcPrompt?.stage}</b>. Recommended template
              <span className="text-emerald-700 font-semibold"> ★ {qcPrompt?.template?.name}</span>.
              Fill the checkpoints below or dismiss to skip.
            </DialogDescription>
          </DialogHeader>
          {qcPrompt && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Result</Label>
                  <Select value={qcPrompt.result} onValueChange={v => setQcPrompt({...qcPrompt, result: v})}>
                    <SelectTrigger data-testid="qc-prompt-result"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {["Passed", "Failed", "Pending"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Moisture %</Label>
                  <Input type="number" value={qcPrompt.moisture} onChange={e => setQcPrompt({...qcPrompt, moisture: e.target.value})}/>
                </div>
              </div>
              {qcPrompt.checks?.length > 0 && (
                <div className="border rounded-md p-3 bg-muted/20 space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Checkpoints ({qcPrompt.checks.length})
                  </div>
                  {qcPrompt.checks.map((c, i) => (
                    <div key={i} className="grid grid-cols-3 gap-2 items-center">
                      <Label className="col-span-1 text-xs">
                        {c.label}{c.required && <span className="text-red-500">*</span>}
                        {c.unit ? ` (${c.unit})` : ""}
                      </Label>
                      <div className="col-span-2">
                        {c.kind === "boolean" ? (
                          <Select value={String(c.value)} onValueChange={v => updQcCheck(i, v === "true")}>
                            <SelectTrigger><SelectValue placeholder="Yes/No"/></SelectTrigger>
                            <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                          </Select>
                        ) : c.kind === "select" ? (
                          <Select value={c.value || ""} onValueChange={v => updQcCheck(i, v)}>
                            <SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger>
                            <SelectContent>{(c.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (
                          <Input type={c.kind === "number" ? "number" : "text"}
                                  value={c.value ?? ""} onChange={e => updQcCheck(i, e.target.value)}
                                  data-testid={`qc-prompt-check-${c.key}`}/>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div><Label>Notes / CAPA</Label>
                <Textarea rows={2} value={qcPrompt.notes} onChange={e => setQcPrompt({...qcPrompt, notes: e.target.value})}/>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setQcPrompt(null)} data-testid="qc-prompt-skip">Skip for now</Button>
            <Button onClick={submitQc} data-testid="qc-prompt-submit">Record QC</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
